/**
 * MultiChoiceFieldPreview Component
 *
 * Preview component for multichoice field type using Design System components.
 *
 * Supports: Chip (custom), Checkbox (DS), Radio Button (DS), and Dropdown (DS)
 * styles with comprehensive configuration options.
 *
 * Chip style supports:
 * - Placeholder images (PlaceHolderIconChip)
 * - Size variants (Compact, Medium, Large)
 * - Image display modes (Fit, Fill, Stretch)
 * - Layout styles (Standard, Padded Frame)
 * - Label positions (Right, Below, Hover, Hidden)
 * - Caption alignment (Left, Centre, Right)
 * - Chip width (Fixed, Dynamic)
 * - Responsive layout for narrow containers
 */

import React from 'react';
import { MultiChoiceFieldPreviewProps } from './types';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import Radio from 'Components/DesignSystem/Radio/Radio';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { MultiChoiceIcon, PlaceHolderIconChip, PlaceholderImageIcon } from 'assests/GmsIcons';
import {
  ChipSize,
  ImageDisplay,
  LayoutStyle,
  TextAlignment,
  ChipWidth,
  ChipLabelPosition,
  Choice,
} from '../../config/fieldDefaults/multichoice.config';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get padding classes based on chip size
 */
const getSizePadding = (size: ChipSize): string => {
  switch (size) {
    case 'Large':
      return 'tw-px-4 tw-py-3';
    case 'Medium':
      return 'tw-px-3 tw-py-3';
    case 'Compact':
      return 'tw-px-2 tw-py-2';
    default:
      return 'tw-px-3 tw-py-3';
  }
};

/**
 * Get chip width classes based on chipWidth and chipSize
 */
const getChipWidthClasses = (chipWidth: ChipWidth, chipSize: ChipSize): string => {
  if (chipWidth === 'Fixed') {
    switch (chipSize) {
      case 'Large':
        return 'tw-min-w-[140px]';
      case 'Medium':
        return 'tw-min-w-[120px]';
      case 'Compact':
        return 'tw-min-w-[80px]';
      default:
        return 'tw-min-w-[120px]';
    }
  }
  // Dynamic width - allow shrinking
  return 'tw-min-w-0';
};

/**
 * Get image size classes based on chip size
 */
const getImageSizeClasses = (chipSize: ChipSize): string => {
  switch (chipSize) {
    case 'Large':
      return 'tw-w-24 tw-h-24';
    case 'Medium':
      return 'tw-w-20 tw-h-20';
    case 'Compact':
      return 'tw-w-12 tw-h-12';
    default:
      return 'tw-w-20 tw-h-20';
  }
};

/**
 * Get image display styles based on imageDisplay mode.
 * Uses [&>svg] selector to force the fixed-size PlaceHolderIconChip SVG
 * (72x56) to respect wrapper dimensions.
 * - Fit: icon centered with visible grey padding (letterbox effect)
 * - Fill: icon fills the container edge-to-edge
 * - Stretch: icon stretches to fill, ignoring aspect ratio
 */
const getImageDisplayStyles = (imageDisplay: ImageDisplay): { containerClass: string; iconClass: string } => {
  // Common: force SVG to fill its wrapper
  const svgFill = '[&>svg]:tw-w-full [&>svg]:tw-h-full';
  switch (imageDisplay) {
    case 'Fill':
      return {
        containerClass: '',
        iconClass: `tw-w-full tw-h-full ${svgFill}`,
      };
    case 'Fit':
    default:
      return {
        containerClass: 'tw-bg-surface-sunken',
        iconClass: `tw-w-3/5 tw-h-3/5 ${svgFill}`,
      };
  }
};

/**
 * Get text alignment class based on captionAlignment
 */
const getTextAlignmentClass = (alignment: TextAlignment): string => {
  switch (alignment) {
    case 'Centre':
      return 'tw-text-center';
    case 'Right':
      return 'tw-text-right';
    case 'Left':
    default:
      return 'tw-text-left';
  }
};

/**
 * Get layout configuration based on chipLabelPosition
 */
const getChipLayout = (
  position: ChipLabelPosition,
  showImage: boolean
): { isColumn: boolean; isHoverLabel: boolean; hideLabel: boolean } => {
  if (!showImage) {
    return { isColumn: false, isHoverLabel: false, hideLabel: false };
  }

  switch (position) {
    case 'Below Image':
      return { isColumn: true, isHoverLabel: false, hideLabel: false };
    case 'Show only on hover':
      return { isColumn: true, isHoverLabel: true, hideLabel: false };
    case 'Hide label':
      return { isColumn: true, isHoverLabel: false, hideLabel: true };
    case 'Right side of image':
    default:
      return { isColumn: false, isHoverLabel: false, hideLabel: false };
  }
};

// ============================================================================
// Main Component
// ============================================================================

export const MultiChoiceFieldPreview = ({
  config,
  isSelected,
  onClick,
  fieldName,
  additionalData,
}: MultiChoiceFieldPreviewProps) => {
  const {
    label,
    displayName,
    showTitle = true,
    style,
    layoutDirection = 'Horizontal',
    chipSize = 'Medium',
    choices = [],
    // Additional chip configuration options
    showImage = false,
    imageDisplay = 'Fit',
    layoutStyle = 'Standard',
    chipLabelPosition = 'Right side of image',
    captionAlignment = 'Left',
    chipWidth = 'Dynamic',
  } = config;

  // Compute display label following existing UI model pattern
  const displayLabel = showTitle
    ? (displayName || label || fieldName || 'Unnamed Field')
    : '';

  // Convert choices to Dropdown options format
  const dropdownOptions = choices.map((choice) => ({
    value: choice.id || choice.value,
    label: choice.label || choice.value,
  }));

  // Get layout configuration for chips
  const chipLayout = getChipLayout(chipLabelPosition, showImage);

  /**
   * Render a single chip with all configuration options
   */
  const renderChip = (choice: Choice | null, index: number) => {
    const chipId = choice?.id || `chip-${index}`;
    const chipValue = choice?.label || choice?.value || `Option ${index + 1}`;

    // Size-based classes
    const imageSizeClass = getImageSizeClasses(chipSize);
    const imageDisplayStyles = getImageDisplayStyles(imageDisplay);
    const paddingClass = getSizePadding(chipSize);
    const widthClass = getChipWidthClasses(chipWidth, chipSize);

    // Layout direction based on chipLabelPosition
    const layoutClass = chipLayout.isColumn
      ? 'tw-flex-col tw-items-center'
      : 'tw-flex-row tw-items-center';

    // Border style based on layoutStyle
    const borderClass = layoutStyle === 'Padded Frame'
      ? 'tw-border-2 tw-border-edge-strong tw-shadow-sm'
      : 'tw-border tw-border-edge';

    // Text alignment based on captionAlignment
    const textAlignClass = getTextAlignmentClass(captionAlignment);

    // Hover label visibility
    const labelVisibilityClass = chipLayout.isHoverLabel
      ? 'tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity'
      : '';

    return (
      <div
        key={chipId}
        className={`
          tw-group
          tw-flex ${layoutClass}
          tw-gap-2
          tw-bg-surface
          tw-rounded
          ${borderClass}
          ${paddingClass}
          ${widthClass}
        `}
      >
        {/* Image placeholder */}
        {showImage && (
          <div className="tw-flex-shrink-0">
            <div
              className={`
                ${imageSizeClass}
                tw-overflow-hidden
                tw-rounded
                tw-flex
                tw-items-center
                tw-justify-center
                tw-border
                tw-border-edge
                ${imageDisplayStyles.containerClass}
              `}
            >
              <div className={`${imageDisplayStyles.iconClass} tw-text-ink-tertiary [&_path]:tw-stroke-current [&_rect]:tw-stroke-current`}>
                <PlaceHolderIconChip />
              </div>
            </div>
          </div>
        )}

        {/* Label */}
        {!chipLayout.hideLabel && (
          <span
            className={`
              chip-label
              tw-text-ink
              tw-text-xs
              tw-font-medium
              ${textAlignClass}
              ${labelVisibilityClass}
              ${showImage && !chipLayout.isColumn ? 'tw-ml-1' : ''}
              ${chipSize === 'Large' ? 'tw-w-full' : ''}
              tw-overflow-hidden
              tw-text-ellipsis
              tw-whitespace-nowrap
            `}
            title={chipValue}
          >
            {chipValue}
          </span>
        )}
      </div>
    );
  };

  /**
   * Render chips container with responsive layout
   */
  const renderChips = () => {
    const displayChoices = choices.length > 0 ? choices.slice(0, 2) : null;

    // Container classes with responsive behavior
    // - Stack vertically at narrow widths (default)
    // - Apply configured layout direction at sm breakpoint
    const containerClasses = `
      tw-flex
      tw-gap-4
      tw-flex-col
      sm:${layoutDirection === 'Vertical' ? 'tw-flex-col' : 'tw-flex-row tw-flex-wrap'}
    `;

    return (
      <div className={containerClasses}>
        {displayChoices
          ? displayChoices.map((choice, index) => renderChip(choice, index))
          : (
            <>
              {renderChip(null, 0)}
              {renderChip(null, 1)}
            </>
          )
        }
      </div>
    );
  };

  // Render by style using Design System components
  const renderByStyle = () => {
    switch (style) {
      case 'Dropdown':
        return (
          <Dropdown
            label=""
            options={dropdownOptions}
            disabled
            placeholder="Select an option"
          />
        );

      case 'Checkbox':
        return (
          <div className={`tw-flex tw-gap-3 ${layoutDirection === 'Vertical' ? 'tw-flex-col' : 'tw-flex-row tw-flex-wrap'}`}>
            {choices.length > 0 ? (
              choices.slice(0, 2).map((choice) => (
                <div key={choice.id || choice.value} className="tw-flex tw-items-center tw-gap-1">
                  <Checkbox
                    label={showImage ? '' : (choice.label || choice.value)}
                    checked={false}
                    onChange={() => {}}
                    disabled
                  />
                  {showImage && <span className="tw-text-ink-tertiary [&_path]:tw-stroke-current [&_rect]:tw-stroke-current"><PlaceholderImageIcon /></span>}
                  {showImage && (
                    <span className="tw-text-sm tw-text-ink">{choice.label || choice.value}</span>
                  )}
                </div>
              ))
            ) : (
              <>
                <div className="tw-flex tw-items-center tw-gap-1">
                  <Checkbox label={showImage ? '' : 'Option 1'} checked={false} onChange={() => {}} disabled />
                  {showImage && <span className="tw-text-ink-tertiary [&_path]:tw-stroke-current [&_rect]:tw-stroke-current"><PlaceholderImageIcon /></span>}
                  {showImage && <span className="tw-text-sm tw-text-ink">Option 1</span>}
                </div>
                <div className="tw-flex tw-items-center tw-gap-1">
                  <Checkbox label={showImage ? '' : 'Option 2'} checked={false} onChange={() => {}} disabled />
                  {showImage && <span className="tw-text-ink-tertiary [&_path]:tw-stroke-current [&_rect]:tw-stroke-current"><PlaceholderImageIcon /></span>}
                  {showImage && <span className="tw-text-sm tw-text-ink">Option 2</span>}
                </div>
              </>
            )}
          </div>
        );

      case 'Radio Button':
        return (
          <div className={`tw-flex tw-gap-3 ${layoutDirection === 'Vertical' ? 'tw-flex-col' : 'tw-flex-row tw-flex-wrap'}`}>
            {choices.length > 0 ? (
              choices.slice(0, 2).map((choice) => (
                <div key={choice.id || choice.value} className="tw-flex tw-items-center tw-gap-1">
                  <Radio
                    label={showImage ? '' : (choice.label || choice.value)}
                    value={choice.id || choice.value}
                    checked={false}
                    onChange={() => {}}
                    disabled
                  />
                  {showImage && <span className="tw-text-ink-tertiary [&_path]:tw-stroke-current [&_rect]:tw-stroke-current"><PlaceholderImageIcon /></span>}
                  {showImage && (
                    <span className="tw-text-sm tw-text-ink">{choice.label || choice.value}</span>
                  )}
                </div>
              ))
            ) : (
              <>
                <div className="tw-flex tw-items-center tw-gap-1">
                  <Radio label={showImage ? '' : 'Option 1'} value="1" checked={false} onChange={() => {}} disabled />
                  {showImage && <span className="tw-text-ink-tertiary [&_path]:tw-stroke-current [&_rect]:tw-stroke-current"><PlaceholderImageIcon /></span>}
                  {showImage && <span className="tw-text-sm tw-text-ink">Option 1</span>}
                </div>
                <div className="tw-flex tw-items-center tw-gap-1">
                  <Radio label={showImage ? '' : 'Option 2'} value="2" checked={false} onChange={() => {}} disabled />
                  {showImage && <span className="tw-text-ink-tertiary [&_path]:tw-stroke-current [&_rect]:tw-stroke-current"><PlaceholderImageIcon /></span>}
                  {showImage && <span className="tw-text-sm tw-text-ink">Option 2</span>}
                </div>
              </>
            )}
          </div>
        );

      case 'Chip':
      default:
        // Enhanced chip renderer with all configuration options
        return renderChips();
    }
  };

  return (
    <div role="button"
      className={`tw-p-4 tw-border tw-rounded-lg tw-bg-surface tw-cursor-pointer
        tw-transition-all tw-duration-200 hover:tw-shadow-sm
        ${isSelected
          ? 'tw-border-accent tw-ring-2 tw-ring-accent-tint'
          : 'tw-border-edge hover:tw-border-edge-strong'
        }`}
      style={{ containerType: 'inline-size' }}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      data-testid="multichoice-field-preview"
    >
      {/* Container query styles for narrow containers */}
      <style>{`
        @container (max-width: 200px) {
          .chip-label {
            font-size: 10px !important;
          }
        }
      `}</style>
      {/* Header with field name, icon, and type badge */}
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mb-1 tw-overflow-hidden">
        <div className="tw-flex tw-items-center tw-gap-1.5 tw-min-w-0 tw-flex-1">
          <span className="tw-flex-shrink-0 tw-w-3 tw-h-3 tw-flex tw-items-center tw-justify-center tw-text-ink-secondary [&_path]:tw-stroke-current">
            <MultiChoiceIcon />
          </span>
          {showTitle ? (
            <Typography variant="sub-title-label" className="tw-text-ink tw-truncate">
              {displayLabel}
            </Typography>
          ) : (
            <Typography variant="sub-title-label" className="tw-text-ink-tertiary tw-italic tw-truncate">
              Hidden
            </Typography>
          )}
          {config.showInfoForField && additionalData?.info && (
            <span
              className="tw-flex-shrink-0 tw-text-[10px] tw-leading-none tw-px-1 tw-py-px tw-bg-accent-tint tw-text-accent tw-rounded-full tw-font-medium"
              title={additionalData.info}
            >
              ℹ
            </span>
          )}
        </div>
        <span className="tw-flex-shrink-0 tw-text-[8px] tw-px-1.5 tw-py-px tw-bg-surface-sunken tw-text-ink-secondary tw-rounded-full tw-uppercase tw-font-medium">
          MultiChoice
        </span>
      </div>

      {/* Description line (from model spec additionalData) */}
      {additionalData?.description && (
        <p className="tw-text-[11px] tw-text-ink-secondary tw-mt-0 tw-mb-2 tw-truncate" title={additionalData.description}>
          {additionalData.description}
        </p>
      )}

      {/* Render style-specific content */}
      {renderByStyle()}
    </div>
  );
};

export default MultiChoiceFieldPreview;
